<template>
  <Teleport to="body">
    <button
      ref="buttonRef"
      class="theme-toggle btn"
      style="position: fixed !important; right: 20px !important; bottom: 82px !important; top: auto !important; left: auto !important; z-index: 2200 !important;"
      type="button"
      :aria-label="theme === 'light' ? 'Use dark theme' : 'Use light theme'"
      @click="handleToggle"
    >
      <span class="theme-toggle-icon" aria-hidden="true"></span>
      <span>{{ theme === 'light' ? 'Dark' : 'Light' }}</span>
    </button>
  </Teleport>
</template>

<script>
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

export default {
  name: 'ThemeToggle',
  props: {
    theme: {
      type: String,
      required: true,
    },
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const buttonRef = ref(null);

    const pin = () => {
      if (!buttonRef.value) return;
      const isMobile = window.innerWidth <= 768;
      buttonRef.value.style.setProperty('position', 'fixed', 'important');
      buttonRef.value.style.setProperty('right', isMobile ? '14px' : '20px', 'important');
      buttonRef.value.style.setProperty('bottom', isMobile ? '76px' : '82px', 'important');
      buttonRef.value.style.setProperty('top', 'auto', 'important');
      buttonRef.value.style.setProperty('left', 'auto', 'important');
      buttonRef.value.style.setProperty('z-index', '2200', 'important');
    };

    watch(() => props.theme, () => {
      nextTick(() => pin());
    });

    const handleToggle = () => {
      emit('toggle');
      requestAnimationFrame(pin);
      setTimeout(pin, 0);
      setTimeout(pin, 120);
    };

    onMounted(() => {
      pin();
      window.addEventListener('resize', pin);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', pin);
    });

    return {
      buttonRef,
      handleToggle,
    };
  },
};
</script>

<style scoped>
.theme-toggle {
  position: fixed !important;
  right: 20px !important;
  bottom: 82px !important;
  top: auto !important;
  left: auto !important;
  z-index: 2200 !important;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 92px;
  min-height: 42px;
  padding: 8px 13px;
  color: var(--app-text);
  cursor: pointer;
  background: var(--app-surface-strong);
  border: 1px solid var(--app-border);
  border-radius: 999px;
  box-shadow: var(--app-shadow-soft);
  backdrop-filter: blur(14px);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.theme-toggle:hover,
.theme-toggle:focus-visible {
  border-color: var(--app-accent);
  box-shadow: var(--app-shadow);
  transform: translateY(-1px);
}

.theme-toggle-icon {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--app-warning), var(--app-accent));
  box-shadow: inset -5px -5px 0 rgba(16, 32, 47, 0.24);
}

:global(:root[data-theme="dark"]) .theme-toggle-icon,
:global(body[data-theme="dark"]) .theme-toggle-icon {
  background: linear-gradient(135deg, #e5e7eb, #94a3b8);
  box-shadow: inset 6px -3px 0 #101827;
}

@media (max-width: 768px) {
  .theme-toggle {
    right: 14px !important;
    bottom: 76px !important;
    min-width: 78px;
    padding: 7px 10px;
    font-size: 13px;
  }
}
</style>
